/**
 * User, Authentication, and Role Types
 */

import type { ReactNode } from 'react';

/** ContactStatus values, exactly as the API sends and accepts them (case-sensitive). */
export const CONTACT_STATUS = {
  ACTIVE: 'Active',
  INACTIVE: 'Inactive'
} as const;

export const CONTACT_STATUS_VALUES = [CONTACT_STATUS.ACTIVE, CONTACT_STATUS.INACTIVE] as const;

export type ContactStatus = (typeof CONTACT_STATUS_VALUES)[number];

/** UserID of a Contact-User that is not (yet) a user: the stored default (#805, #806). */
export const NON_USER_ID = 'NON-USER';

// User types with role-based permissions
export interface User {
  id: number;
  first_name: string;
  last_name: string;
  preferred_name?: string | null;
  email: string;
  roles: UserRole[]; // Multi-role support: array of assigned roles
  division_id?: number | null;
  division_name?: string | null;
  is_active?: boolean;
  // Contact-User fields (CTU-1.1). Optional here; AdminUser narrows them.
  contact_id?: string | null;
  user_id?: string;
  contact_status?: ContactStatus | null;
  department?: string | null;
}

/** Query for GET /api/users/search, the CO/CM picker's paged user search (ADO-1186) */
export interface UserSearchParams {
  /** Every word must match a name or the email; blank means no search */
  search?: string;
  /** Exactly these users, active or inactive; without ids only active users are returned */
  ids?: number[];
  limit?: number;
  offset?: number;
}

export type UserRole =
  | 'Admin'
  | 'Assessor'
  | 'Contract Role'
  | 'View-only'
  | 'Scheduler'
  | 'Program Lead'
  | 'Executive';

/**
 * Role constants - use these instead of hardcoded strings
 * Mirrors backend api/constants/roles.ts
 */
export const ROLES = {
  ADMIN: 'Admin' as UserRole,
  ASSESSOR: 'Assessor' as UserRole,
  CONTRACT_ROLE: 'Contract Role' as UserRole,
  VIEW_ONLY: 'View-only' as UserRole,
  SCHEDULER: 'Scheduler' as UserRole,
  PROGRAM_LEAD: 'Program Lead' as UserRole,
  EXECUTIVE: 'Executive' as UserRole
} as const;

/**
 * Role groups for common role combinations
 * Mirrors backend api/constants/roles.ts ROLE_GROUPS
 */
export const ROLE_GROUPS = {
  /** Full system access (Program Lead is Admin-equivalent for MVP) */
  FULL_ACCESS: [ROLES.ADMIN, ROLES.PROGRAM_LEAD] as UserRole[],

  /**
   * Settings and administration Program Lead must not reach: notifications, email templates, import,
   * system parameters, job schedules, reference lists, supplier tiers, Scope Rules, Audit Log export
   * (ADO-1176, ADO-1187)
   */
  ADMIN_ONLY: [ROLES.ADMIN] as UserRole[],

  /** Can create assessments - Program Lead updates, completes and cancels but does not create (ADO-1176) */
  ASSESSMENT_CREATORS: [ROLES.ADMIN, ROLES.ASSESSOR] as UserRole[],

  /** First-time meeting scheduling and bundling - Program Lead may only reschedule/cancel (ADO-1176) */
  MEETING_SCHEDULERS: [ROLES.ADMIN, ROLES.SCHEDULER, ROLES.ASSESSOR] as UserRole[],

  /** Contact-User Notes and History in the UI, read-only except for Admin (ADO-827, ADO-1187) */
  CONTACT_USER_RECORD_CONTEXT_VIEWERS: [ROLES.ADMIN, ROLES.PROGRAM_LEAD, ROLES.SCHEDULER, ROLES.ASSESSOR] as UserRole[],

  /** User Verification List (Pending Contact-Users); Program Lead has no access (ADO-1176) */
  USER_VERIFICATION_VIEWERS: [ROLES.ADMIN, ROLES.SCHEDULER, ROLES.ASSESSOR] as UserRole[],

  /** Can view assessments */
  ASSESSMENT_VIEWERS: [
    ROLES.ADMIN,
    ROLES.PROGRAM_LEAD,
    ROLES.ASSESSOR,
    ROLES.CONTRACT_ROLE,
    ROLES.EXECUTIVE
  ] as UserRole[],

  /** Can edit assessment items (update evaluations) */
  ASSESSMENT_EDITORS: [ROLES.ADMIN, ROLES.PROGRAM_LEAD, ROLES.ASSESSOR] as UserRole[],

  /** Can upload evidence for gaps */
  EVIDENCE_UPLOADERS: [ROLES.ADMIN, ROLES.PROGRAM_LEAD, ROLES.CONTRACT_ROLE] as UserRole[],

  /** Can review/approve/reject evidence */
  EVIDENCE_REVIEWERS: [ROLES.ADMIN, ROLES.PROGRAM_LEAD, ROLES.ASSESSOR] as UserRole[],

  /**
   * Has access to the scheduler dashboard / Pending CO/CM worklist. Scheduler-only: Admin (ADO-1116) and
   * Program Lead (ADO-1146) are excluded unless they also hold the Scheduler role
   */
  SCHEDULER_ACCESS: [ROLES.SCHEDULER] as UserRole[],

  /** Can access the Query module (build/execute/manage queries) */
  QUERY_ACCESS: [ROLES.ADMIN, ROLES.ASSESSOR, ROLES.SCHEDULER, ROLES.PROGRAM_LEAD] as UserRole[],

  /** Can list/view/export Contact-Users (ADO-1144, ADO-1145); writes stay CONTACT_USER_MANAGERS */
  CONTACT_USER_VIEWERS: [ROLES.ADMIN, ROLES.PROGRAM_LEAD, ROLES.SCHEDULER, ROLES.ASSESSOR] as UserRole[],

  /** Can create/change Contact-Users; Program Lead views only and must ask an Admin (ADO-1143) */
  CONTACT_USER_MANAGERS: [ROLES.ADMIN] as UserRole[],

  /** Can create suppliers (ADO-1140) — Program Lead must request from Admin */
  SUPPLIER_CREATORS: [ROLES.ADMIN] as UserRole[],

  /** Can delete suppliers (ADO-1141) — Program Lead must request from Admin */
  SUPPLIER_DELETERS: [ROLES.ADMIN] as UserRole[],

  /** Can update suppliers (create/delete are Admin-only) */
  SUPPLIER_MANAGERS: [ROLES.ADMIN, ROLES.PROGRAM_LEAD, ROLES.SCHEDULER, ROLES.ASSESSOR] as UserRole[],

  /** Executive-level access for dashboards/reports */
  EXECUTIVE_ACCESS: [ROLES.ADMIN, ROLES.PROGRAM_LEAD, ROLES.EXECUTIVE] as UserRole[],

  /** Contract Role dashboard / evidence home */
  CONTRACT_ROLE_ACCESS: [ROLES.ADMIN, ROLES.PROGRAM_LEAD, ROLES.CONTRACT_ROLE] as UserRole[],

  /** Internal users (not Contract Role) */
  INTERNAL_USERS: [
    ROLES.ADMIN,
    ROLES.PROGRAM_LEAD,
    ROLES.EXECUTIVE,
    ROLES.ASSESSOR,
    ROLES.SCHEDULER
  ] as UserRole[],

  /** External users */
  EXTERNAL_USERS: [ROLES.CONTRACT_ROLE] as UserRole[],

  /** Full access to reports */
  REPORTS_FULL_ACCESS: [ROLES.ADMIN, ROLES.PROGRAM_LEAD, ROLES.EXECUTIVE, ROLES.SCHEDULER] as UserRole[],

  /** Can view contracts (Scheduler needs the list as a prerequisite for scheduling, #1127) */
  CONTRACT_VIEWERS: [
    ROLES.ADMIN,
    ROLES.PROGRAM_LEAD,
    ROLES.CONTRACT_ROLE,
    ROLES.ASSESSOR,
    ROLES.SCHEDULER
  ] as UserRole[],

  /** Can create contracts (ADO-1142) — Program Lead cannot create; UI is Admin-only */
  CONTRACT_CREATORS: [ROLES.ADMIN] as UserRole[],

  /** Can update existing contracts (create stays Admin-only in the UI; Contract Role is evidence-only) */
  CONTRACT_EDITORS: [ROLES.ADMIN, ROLES.PROGRAM_LEAD, ROLES.SCHEDULER] as UserRole[],

  /** Can hard-delete contracts with no linked meetings or assessments (ADO-743) — Admin only */
  CONTRACT_DELETERS: [ROLES.ADMIN] as UserRole[],

  /** Entity notes - can view and create entity notes (excludes Contract Role) */
  NOTES_ACCESS: [
    ROLES.ADMIN,
    ROLES.PROGRAM_LEAD,
    ROLES.ASSESSOR,
    ROLES.SCHEDULER,
    ROLES.EXECUTIVE,
    ROLES.VIEW_ONLY
  ] as UserRole[]
} as const;

// Authentication types
export interface AuthResponse {
  message: string;
  token: string;
  user: User;
}

export interface LoginCredentials {
  email: string;
  password: string;
}

// Assessor workload for assignment modal (from backend)
export interface AssessorWorkload {
  assessorId: number;
  assessorName: string;
  totalMeetings: number;
  scheduledMeetings: number;
  pendingMeetings: number;
  completedMeetings: number;
}

/** One labelled value on the Contact-User detail page */
export interface ContactUserField {
  label: string;
  value: ReactNode;
}
